import React, {useState, useEffect, useRef, useCallback} from 'react'
import {Search, Edit2} from "react-feather";
import {useFormContext} from "react-hook-form";
import {get, post} from "../../services/ApiService";
import {useWindowManager} from "../Window";
import {resolveWindowSize} from "../Window/windowTypes";
import Grid from "../Core/Grid";
import componentRegistry from "../Core/ComponentRegistry";
import {useNotification} from "../Core/NotificationProvider";
import {labelSizeClass} from "./labelSize";

export default function UiInputSuggest({ label, name, idField, titleField, size, labelSize, router, onSelect, initialFilter, formComponent, extraData, disabled, ...rest }: any){
    const windowManager = useWindowManager();
    const notification = useNotification();
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const debounceTimerRef = useRef<any>(null);
    const { setValue, register, watch } = useFormContext();
    const value = watch(name);
    const idValue = watch(`${name}.${idField}`);
    const titleValue = watch(`${name}.${titleField}`);

    const triggerSelect = useCallback((data: any) => {
        Object.keys(data).forEach((keyItem: any) => {
            if(keyItem.indexOf('/') !== -1){
                const exploded = keyItem.split('/');
                if(!data[exploded[0]]) data[exploded[0]] = {};
                data[exploded[0]][exploded[1]] = data[keyItem];
            }
        });

        setValue(name, data);
        setValue(`${name}.${idField}`, data?.[idField] ?? '');
        setValue(`${name}.${titleField}`, data?.[titleField] ?? '');
        if(onSelect) onSelect(data);
    }, [name, idField, titleField, setValue, onSelect]);

    const openGrid = useCallback(() => {
        let windowId: string;

        const handleSelect = (data: any) => {
            windowManager.closeWindow(windowId);
            triggerSelect(data);
        };

        windowId = windowManager.openWindow({
            title: label,
            component: Grid,
            size: resolveWindowSize('xl'),
            props: {
                router,
                initialFilter,
                extraData,
                formComponent,
                selectionMode: 'single',
                triggerSelect: handleSelect,
            }
        });
    }, [windowManager, label, router, initialFilter, extraData, formComponent, triggerSelect]);

    const formEntry = componentRegistry[router];
    const podeEditar = !!formEntry?.form && !!idValue && !disabled;

    const openEdit = async () => {
        const id = idValue;
        const loadingId = notification.showLoading('Carregando...');
        let editData: any;
        try {
            const response = await get(`/?route=${router}/form&action=edit&rows=${encodeURIComponent(JSON.stringify([{[idField]: id}]))}`);
            notification.hideLoading(loadingId);
            if (response.status !== 200 || !response.data?.success) {
                notification.error(response.data?.message || 'Erro ao carregar dados');
                return;
            }
            editData = response.data.payload;
        } catch (error: any) {
            notification.hideLoading(loadingId);
            notification.error(error.message || 'Erro ao carregar dados');
            return;
        }

        const FormComponent = formEntry.form as React.ComponentType<any>;
        let windowId: string;

        const handleSubmit = async (formData: any) => {
            const savingId = notification.showLoading('Salvando...');
            try {
                const response = await post(`/?route=${router}/form&action=edit`, { dados: formData });
                notification.hideLoading(savingId);
                if (response.status === 200 && response.data?.success) {
                    windowManager.closeWindow(windowId);
                    notification.success(response.data?.message || 'Salvo com sucesso');
                    searchById(String(id), true);
                } else {
                    notification.error(response.data?.message || 'Erro ao salvar');
                }
            } catch (error: any) {
                notification.hideLoading(savingId);
                notification.error(error.message || 'Erro ao salvar');
            }
        };

        windowId = windowManager.openWindow({
            title: formEntry.title || label,
            component: () => <FormComponent data={editData} loading={false} triggerSubmit={handleSubmit} triggerClose={() => windowManager.closeWindow(windowId)} />,
            size: resolveWindowSize(formEntry.windowSize ?? 'lg'),
            loading: false,
            data: null
        });
    };

    // Buscar por ID com debounce
    const searchById = async (id: string, forcar = false) => {
        if (!id?.trim()) return;
        if (!forcar && id === String(value?.[idField])) return;

        setIsSearching(true);
        try {
            const filterValue = [{
                name: idField,
                operator: 'eq',
                type: 'number',
                value: id.trim()
            }];

            const response = await get(`/?route=${router}/grid&action=list&skip=0&limit=1&filterValue=${JSON.stringify(filterValue)}`);

            if (response.data?.rows && response.data.rows.length > 0) {
                triggerSelect(response.data.rows[0]);
            } else {
                alert(`Registro com ID ${id} não encontrado`);
                setValue(`${name}.${idField}`, '');
                setValue(`${name}.${titleField}`, '');
                setValue(name, null);
            }
        } catch (error) {
            console.error('Erro ao buscar por ID:', error);
            alert('Erro ao buscar registro');
        }
        setIsSearching(false);
    };

    // Handler com debounce para o campo ID
    const handleIdChange = (e: any) => {
        const id = e.target.value;

        // Limpa o timer anterior
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Se vazio, não faz nada
        if (!id?.trim()) return;

        // Cria novo timer (200ms de debounce)
        debounceTimerRef.current = setTimeout(() => {
            searchById(id);
        }, 200);
    };

    // Handler de teclado para o campo ID
    const handleIdKeyDown = (e: any) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
            // Cancela o debounce pendente
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            // Executa busca imediatamente
            const id = e.target.value;
            if (id?.trim()) {
                searchById(id);
            }
        }
    };

    // Handler onBlur do campo ID - limpa tudo se estiver vazio
    const handleIdBlur = (e: any) => {
        const id = e.target.value;
        if (!id || !id.trim()) {
            // Se o ID está vazio, limpa o campo inteiro
            setValue(name, null);
            setValue(`${name}.${idField}`, '');
            setValue(`${name}.${titleField}`, '');
        }
    };

    // Buscar sugestões com debounce
    const searchSuggestions = async (searchText: string) => {
        if (!searchText || searchText.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            setSelectedIndex(-1);
            return;
        }

        setIsSearching(true);
        try {
            const filterValue = [{
                name: titleField,
                operator: 'contains',
                type: 'string',
                value: searchText
            }];
            const response = await get(`/?route=${router}/grid&action=list&skip=0&limit=10&filterValue=${JSON.stringify(filterValue)}`);
            if (response.data?.rows) {
                setSuggestions(response.data.rows);
                setShowSuggestions(true);
                setSelectedIndex(-1);
            }
        } catch (error) {
            console.error('Erro ao buscar sugestões:', error);
        }
        setIsSearching(false);
    };

    // Handler com debounce para o campo nome
    const handleTitleChange = (e: any) => {
        const searchText = e.target.value;

        // Limpa o timer anterior
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (!searchText || searchText.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            setSelectedIndex(-1);
            return;
        }

        // Cria novo timer (300ms de debounce)
        debounceTimerRef.current = setTimeout(() => {
            searchSuggestions(searchText);
        }, 300);
    };

    // Navegação por teclado no autocomplete
    const handleTitleKeyDown = (e: any) => {
        // Tab: executa busca imediatamente
        if (e.key === 'Tab') {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            const searchText = e.target.value;
            if (searchText && searchText.length >= 2) {
                searchSuggestions(searchText);
            }
            return;
        }

        // Outras teclas: navegação no autocomplete
        if (!showSuggestions || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
                    selectSuggestion(suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setSelectedIndex(-1);
                break;
        }
    };

    // Selecionar uma sugestão
    const selectSuggestion = (suggestion: any) => {
        triggerSelect(suggestion);
        setShowSuggestions(false);
        setSuggestions([]);
        setSelectedIndex(-1);
    };

    // Cleanup do debounce e fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Scroll automático para o item selecionado
    useEffect(() => {
        if (selectedIndex >= 0 && suggestionsRef.current) {
            const selectedElement = suggestionsRef.current.children[selectedIndex] as HTMLElement;
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [selectedIndex]);

    return (
        <div className={`col-lg-${(size) ? size : 12}`}>
            <div className={`form-item${labelSizeClass(labelSize)}`}>
                <label className="form-label">{label}</label>
                <div className="field-container">
                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                        <input
                            type="text"
                            style={{ width: 70, minWidth: 70, borderRadius: '6px 0 0 6px', borderRight: 'none' }}
                            className="form-control"
                            {...register(`${name}.${idField}`)}
                            onChange={handleIdChange}
                            onKeyDown={handleIdKeyDown}
                            onBlur={handleIdBlur}
                            disabled={disabled}
                        />
                        <button
                            type="button"
                            style={{
                                border: '1px solid #0369a1',
                                borderLeft: 'none',
                                borderRight: 'none',
                                borderRadius: 0,
                                background: '#0369a1',
                                padding: '0 10px',
                                cursor: isSearching ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#fff',
                                opacity: isSearching ? 0.5 : 1,
                                flexShrink: 0,
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#075985')}
                            onMouseLeave={e => (e.currentTarget.style.background = '#0369a1')}
                            onClick={() => openGrid()}
                            disabled={isSearching || disabled}
                        >
                            <Search strokeWidth={2} size={14} />
                        </button>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                type="text"
                                style={{ borderRadius: podeEditar ? 0 : '0 6px 6px 0', borderLeft: 'none', width: '100%' }}
                                className="form-control"
                                {...register(`${name}.${titleField}`)}
                                onChange={handleTitleChange}
                                onKeyDown={handleTitleKeyDown}
                                onFocus={(e) => titleValue && titleValue.length >= 2 && searchSuggestions(titleValue)}
                                autoComplete="off"
                                disabled={disabled}
                            />
                            {showSuggestions && suggestions.length > 0 && (
                                <div
                                    ref={suggestionsRef}
                                    style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        backgroundColor: 'white',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                        zIndex: 1000,
                                        marginTop: '2px'
                                    }}
                                >
                                    {suggestions.map((suggestion: any, index: number) => (
                                        <div
                                            key={index}
                                            onClick={() => selectSuggestion(suggestion)}
                                            onMouseEnter={() => setSelectedIndex(index)}
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                borderBottom: index < suggestions.length - 1 ? '1px solid #eee' : 'none',
                                                transition: 'background-color 0.2s',
                                                backgroundColor: selectedIndex === index ? '#e3f2fd' : 'white',
                                                fontWeight: selectedIndex === index ? 'bold' : 'normal'
                                            }}
                                        >
                                            <strong>#{suggestion[idField]}</strong> - {suggestion[titleField]}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {podeEditar && (
                            <button
                                type="button"
                                title={`Editar ${label}`}
                                style={{
                                    border: '1px solid #0369a1',
                                    borderLeft: 'none',
                                    borderRadius: '0 6px 6px 0',
                                    background: '#0369a1',
                                    padding: '0 10px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#fff',
                                    flexShrink: 0,
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#075985')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#0369a1')}
                                onClick={openEdit}
                            >
                                <Edit2 strokeWidth={2} size={14} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
